import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { Box, Typography } from "@material-ui/core";
import { useFieldValue } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import useStyles from "../containers/data/forms/forms-styles";
import { dark } from "../hooks/use-map-theme-styles";
import { useAvailableGenericNames } from "../hooks/useAvailableGenericNames";
import { useClearEnrollingConditionSet } from "../hooks/useClearEnrollingConditionSet";
import { useScopes } from "../hooks/useScopes";
import { ControlState } from "../models/flexible/control-state";
import { FlexControlProps, FlexFieldController } from "../models/flexible/flex-field-controller";
import { InitialRequestConfig } from "../models/flexible/initial-request";
import { isStartingPoint } from "../models/flexible/typed-flex-entity";
import { combinePaths } from "../models/flexible/utils";
import { Brand, FlexFormStatus, Generic, Maybe } from "../models/generated/graphql";
import { getScopeTreatments } from "../utils/agreement-utils";
import { anyNamedEntity } from "../utils/any-named-entity";
import { formatTreatment } from "../utils/base-agreement-formatters";
import { firstLetterUppercase } from "../utils/first-letter-uppercase";
import { getPathPrefix } from "../utils/get-path-prefix";
import { GeneralFieldController } from "./general-field-controller";
import { GET_SCOPES, whereDrugs } from "./queries";

function TreatmentList(props: { path: string; controller: FlexFieldController }) {
	const [generic] = useFieldValue<Generic>(props.path);
	const [brands] = useFieldValue<Array<Brand>>(combinePaths(getPathPrefix(props.path, 1), AttributeIdentifiers.BrandName));
	const brandNames = brands ? (brands.map((brand: Brand) => brand.Name) as Array<string>) : [];
	const { selectedScopes } = useScopes(props.controller.parent.context);
	const classes = useStyles();
	const { t } = useTranslation();

	const items = selectedScopes
		.flatMap((scope) => getScopeTreatments(scope, (treatmentSet) => treatmentSet.generic.Name === generic?.Name))
		.filter(
			(treatment) =>
				!brandNames.length || brandNames.includes(anyNamedEntity.Name) || brandNames.includes(treatment.regulatedBy.BrandName)
		)
		.map((treatment) => formatTreatment(treatment));

	const distinctItems = Array.from(new Set(items)).sort();

	return (
		<Box className={classes.colLong} pr={5} paddingY={2.5} pl={1.5}>
			<Typography variant="caption" color="textSecondary">
				{firstLetterUppercase(t("packs"))}
			</Typography>
			{!!items.length &&
				distinctItems.map((item, i) => (
					<Box key={i}>
						<Typography variant="body2" style={{ color: dark }} className={classes.readOnlyControlContent}>
							{item}
						</Typography>
					</Box>
				))}
			{!items.length && (
				<Typography variant="body2" style={{ color: dark }} className={classes.readOnlyControlContent}>
					--
				</Typography>
			)}
		</Box>
	);
}

export class GenericNameFieldController extends GeneralFieldController {
	getInitialRequestComponents(): InitialRequestConfig {
		return {
			queries: [GET_SCOPES],
			variables: { where: whereDrugs(this.parent.context.entityType.id, this.parent.context.tenantId) },
		};
	}

	override useControlState(): ControlState {
		if (this.parent.context.flexEntity?.status === FlexFormStatus.Submitted && isStartingPoint(this.parent.context.flexEntity)) {
			return "enabled";
		}

		return this.parent.context.cases?.length || (this.parent.context.isNegotiable && !this.schema.attribute.isNegotiable)
			? "disabled"
			: "enabled";
	}

	override useControlProps(path: string): Maybe<FlexControlProps> | undefined {
		const genericNames = useAvailableGenericNames(this, path);
		const handleClear = useClearEnrollingConditionSet(this.parent.context);

		return {
			autoSelect: false,
			controlType: "typeahead",
			options: genericNames,
			optionLabel: "Name",
			property: "Name",
			onClear: genericNames.length === 1 ? handleClear : undefined,
		};
	}

	override getValueTypeProps() {
		return {
			controlType: "typeahead",
			property: "GenericName",
		};
	}

	renderAfter(path: string): React.ReactElement {
		if (this.parent.context.isNegotiable) {
			return <></>;
		}

		return <TreatmentList path={path} controller={this} />;
	}

	protected override createFieldValidationRuleByDataType(): Yup.AnySchema {
		return Yup.object().nullable().required();
	}
}
